import React, { memo, useState } from 'react';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { H2 } from '@osmosis/tailwind-ui/components/Text';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { borderColor200 } from '@osmosis/tailwind-ui/styles/border';
import { bg1 } from '@osmosis/tailwind-ui/styles/background';

import {
  LoggedOutProductLibraryContainsItem,
  LoggedOutProductLibraryItem,
  SelectedLibraryItem,
} from '../../types/library';
import { FormattedMessage } from 'react-intl';
import { faListUl } from '@fortawesome/pro-solid-svg-icons/faListUl';
import Divider from '../Divider';
import NavStructure from './logged-out/NavStructure';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { useContentSize } from '@osmosis/nextjs-components/context';

type Props = {
  navItems: LoggedOutProductLibraryItem[];
  onSelect: (
    l1: LoggedOutProductLibraryContainsItem,
    l2: LoggedOutProductLibraryContainsItem,
  ) => void;
  selectedItem?: SelectedLibraryItem;
  openIndex?: number;
};

/**
 * Sidebar to display in the logged out library.
 */
const LibrarySidebarLoggedOut = (props: Props) => {
  const { navItems, onSelect, selectedItem, openIndex } = props;

  const [isVisible, setIsVisible] = useState(false);

  const [viewedLibraries, setViewedLibraries] = useState<string[]>([]);

  const handleSelect = (
    l1: LoggedOutProductLibraryContainsItem,
    l2: LoggedOutProductLibraryContainsItem,
  ) => {
    handleMobileToggle();
    onSelect(l1, l2);
  };

  const handleMobileToggle = () => {
    setIsVisible((prev) => !prev);
  };

  const handleViewedLibraries = (libraryLink: string) => {
    setViewedLibraries((prev) => [...new Set([...prev, libraryLink])]);
  };

  // Responsive to the content-area width so the sidebar becomes collapsible
  // (mobile-style) when the Osmosis AI panel narrows the content.
  const { getClassValue } = useContentSize();

  const containerHeightResponsive = getClassValue({
    defaultClasses: 'lg:h-[90vh] h-auto',
    breakpoints: { lg: 'h-[90vh]', base: 'h-auto' },
  });

  const headerTextResponsive = getClassValue({
    defaultClasses: 'lg:text-xl text-lg',
    breakpoints: { lg: 'text-xl', base: 'text-lg' },
  });

  const mobileToggleResponsive = getClassValue({
    defaultClasses: 'lg:hidden block',
    breakpoints: { lg: 'hidden', base: 'block' },
  });

  const navListResponsive = getClassValue({
    defaultClasses: `max-h-[600px] lg:max-h-[80vh] ${
      isVisible ? 'block' : 'hidden'
    } lg:block`,
    breakpoints: {
      lg: 'max-h-[80vh] block',
      base: `max-h-[600px] ${isVisible ? 'block' : 'hidden'}`,
    },
  });

  return (
    <Box
      data-testid="library-sidebar-container-loggedout"
      cn={`${bg1} m-2 border-1 ${borderColor200} rounded-lg z-3 ${containerHeightResponsive}`}
    >
      <Box
        cn={`p-3 w-full flex flex-col gap-2 ${headerTextResponsive}`}
        onClick={handleMobileToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleMobileToggle();
          }
        }}
        data-testid="mobile-sidebar-toggle-header"
      >
        <Flex cn="justify-between">
          <Flex cn="gap-8 items-center">
            <Icon icon={faListUl} size="sm" display="inline" />
            <H2 cn="inline">
              <FormattedMessage
                id="library.sidebar.topics"
                defaultMessage="Topics"
              />
            </H2>
          </Flex>

          <Box cn={`opacity-75 ${mobileToggleResponsive}`}>
            <Icon icon={isVisible ? faChevronUp : faChevronDown} />
          </Box>
        </Flex>
      </Box>

      <Divider
        data-testid="library-sidebar-divider"
        className={`${bg1} mx-auto`}
      />

      <Flex cn={`flex-col p-1 overflow-y-auto ${navListResponsive}`}>
        <Box>
          <NavStructure
            navData={navItems}
            onSelect={handleSelect}
            handleViewedLibraries={handleViewedLibraries}
            viewedLibraries={viewedLibraries}
            selectedItem={selectedItem}
            openIndex={openIndex}
          />
        </Box>
      </Flex>
    </Box>
  );
};

export default memo(LibrarySidebarLoggedOut);
